import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { validateInvite, setPassword as setPasswordApi } from "../services/authService";
import "../assets/css/pages/login.css";

// Startise 4-colour brand mark (kept in sync with login page).
function BrandLogo({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
      <path d="M233.237 355.464C233.237 408.279 190.422 451.072 137.63 451.072C84.8153 451.072 42 408.257 42 355.464H233.237Z" fill="#FDB849" />
      <path d="M177.079 61C102.504 61 42.0476 121.457 42.0476 196.031C42.0476 223.297 50.1305 248.692 64.0499 269.901C69.1596 277.698 75.0621 284.944 81.6033 291.485C106.028 315.91 139.792 330.997 177.079 330.997L272.576 61H177.079Z" fill="#09A7DF" />
      <path d="M470.727 156.608C470.727 103.793 427.912 61 375.12 61C322.305 61 279.512 103.815 279.512 156.608H470.749H470.727Z" fill="#665DA9" />
      <path d="M335.652 451.074C410.227 451.074 470.684 390.617 470.684 316.043C470.684 288.776 462.601 263.382 448.681 242.173C443.572 234.376 437.669 227.13 431.128 220.589C406.703 196.164 372.94 181.077 335.652 181.077L240.155 451.096H335.652V451.074Z" fill="#F0679F" />
    </svg>
  );
}

const SetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // "reset" when reached via a forgot-password link; otherwise a new-admin invite.
  const isReset = params.get("mode") === "reset";

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null); // { name, email, role, status }
  const [invalid, setInvalid] = useState("");
  const [password, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = (isReset ? "Reset your password" : "Set your password") + " · Startise Attendance";
    if (!token) { setInvalid("This link is missing its token."); setChecking(false); return; }
    validateInvite(token).then(({ status, invite, message }) => {
      if (status === 200 && invite) setInvite(invite);
      else setInvalid(message || "This link is invalid or has expired.");
      setChecking(false);
    });
  }, [token, isReset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    const { status, message } = await setPasswordApi(token, password);
    setSubmitting(false);
    if (status === 200) {
      toast.success("Password set — you can sign in now.");
      navigate("/login");
    } else {
      setError(message || "Couldn't set your password. The link may have expired.");
    }
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <span className="blob blob-cyan" />
        <span className="blob blob-pink" />
        <div className="ll-top">
          <BrandLogo />
          <span className="brand">Startise</span>
        </div>
        <div className="ll-mid">
          <div className="ll-tag">Attendance · Admin Console</div>
          <h1>{isReset ? "Reset your password." : "Set your password to get started."}</h1>
          <p>{isReset
            ? "Choose a new password for your attendance admin account."
            : "You've been invited to the attendance admin console. Choose a password to activate your account."}</p>
        </div>
        <div className="ll-foot">© {new Date().getFullYear()} Startise · Internal use only</div>
      </div>

      <div className="login-right">
        <div className="lr-card">
          {checking && <div className="lr-sub">Checking your link…</div>}

          {!checking && invalid && (
            <>
              <div className="lr-title">{isReset ? "Reset link problem" : "Invite link problem"}</div>
              <div className="lr-sub">{invalid}</div>
              <button className="signin-btn" type="button" onClick={() => navigate("/login")} style={{ marginTop: 18 }}>
                Go to sign in
              </button>
            </>
          )}

          {!checking && invite && (
            <>
              <div className="lr-title">{isReset ? "Choose a new password" : `Welcome, ${invite.name}`}</div>
              <div className="lr-sub">
                {isReset ? "Set a new password for " : "Set a password for "}<b>{invite.email}</b>{isReset ? "." : ` (${invite.role}).`}
              </div>

              <form onSubmit={handleSubmit}>
                <label htmlFor="password">New password</label>
                <div className="input-wrap">
                  <svg className="ic"><use href="#i-lock" /></svg>
                  <input id="password" type={showPw ? "text" : "password"} placeholder="At least 8 characters" autoComplete="new-password" value={password} onChange={(e) => setPw(e.target.value)} />
                  <button type="button" className="eye" onClick={() => setShowPw((v) => !v)} aria-label="Show password">
                    <svg><use href={`#i-${showPw ? "eye-off" : "eye"}`} /></svg>
                  </button>
                </div>

                <label htmlFor="confirm">Confirm password</label>
                <div className="input-wrap">
                  <svg className="ic"><use href="#i-lock" /></svg>
                  <input id="confirm" type={showPw ? "text" : "password"} placeholder="Re-enter your password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>

                <button className="signin-btn" type="submit" disabled={submitting}>
                  {submitting ? "Setting password…" : isReset ? "Reset password" : "Set password & continue"}
                </button>

                {error && (
                  <div className="login-err"><svg><use href="#i-warn" /></svg>{error}</div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
